import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, MapPin, Globe, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { fadeUp, stagger } from "@/lib/anim";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "stayflow.admin@gmail.com";
const ADDRESS = "163, Jyoti Nagar, Bongaon, Part - 3, Golaghat, Assam - 785621 (India)";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1621293954908-907159247fc8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export const AboutContact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent", { description: "Thanks for reaching out — we'll get back to you soon." });
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again or email us directly." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="about" className="py-24 md:py-32 scroll-mt-24" data-testid="about-contact">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16 items-stretch">
        {/* LEFT — About + presence */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger(0.1)}
          className="flex flex-col"
        >
          <motion.p variants={fadeUp} className="overline text-brand">About us</motion.p>
          <motion.h2 variants={fadeUp} className="mt-5 font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-ink leading-tight">
            A hospitality OS, built from the ground up.
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-6 text-lg text-ink-soft leading-relaxed max-w-xl">
            StayFlow is made for independent properties and venues — homestays, hotels,
            resorts, restaurants, cafes, bars and lounges. We bring room management,
            point-of-sale, guest records and finances into one calm, mobile-first workspace.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-8 rounded-3xl overflow-hidden aspect-[16/10] border border-black/5 shadow-lg group">
            <img src={ABOUT_IMG} alt="Modern hotel lobby" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          </motion.div>

          <motion.div variants={fadeUp} className="mt-8 space-y-4">
            <a href={`mailto:${EMAIL}`} data-testid="about-email" className="group flex items-center gap-4 rounded-2xl bg-white border border-black/5 p-4 hover:shadow-md transition-shadow">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-brand-soft text-brand shrink-0"><Mail className="w-5 h-5" /></span>
              <span>
                <span className="block text-xs uppercase tracking-widest text-ink-faint">Email</span>
                <span className="block font-medium text-ink group-hover:text-brand transition-colors break-all">{EMAIL}</span>
              </span>
            </a>
            <div className="flex items-center gap-4 rounded-2xl bg-white border border-black/5 p-4">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-brand-soft text-brand shrink-0"><MapPin className="w-5 h-5" /></span>
              <span>
                <span className="block text-xs uppercase tracking-widest text-ink-faint">Registered office</span>
                <span className="block font-medium text-ink" data-testid="about-address">{ADDRESS}</span>
              </span>
            </div>
            <div className="flex items-center gap-4 rounded-2xl bg-white border border-black/5 p-4">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-brand-soft text-brand shrink-0"><Globe className="w-5 h-5" /></span>
              <span>
                <span className="block text-xs uppercase tracking-widest text-ink-faint">Web</span>
                <span className="block font-medium text-ink">www.stayflowapp.in</span>
              </span>
            </div>
          </motion.div>
        </motion.div>

        {/* RIGHT — Contact form */}
        <motion.div
          id="contact"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="scroll-mt-28"
        >
          <div className="sticky top-28 rounded-[2rem] bg-ink text-white p-8 md:p-10 shadow-[0_30px_80px_rgba(20,20,20,0.25)]">
            <p className="overline text-brand">Contact us</p>
            <h3 className="mt-4 font-heading font-extrabold text-3xl tracking-tight">Let's talk about your property</h3>
            <p className="mt-3 text-white/60">Tell us a little about your venue and we'll be in touch.</p>

            <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white/70 text-xs uppercase tracking-widest">Name</Label>
                  <Input id="name" name="name" value={form.name} onChange={onChange} placeholder="Your name" data-testid="contact-name"
                    className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12 focus-visible:ring-brand" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white/70 text-xs uppercase tracking-widest">Email</Label>
                  <Input id="email" name="email" type="email" value={form.email} onChange={onChange} placeholder="you@venue.com" data-testid="contact-email"
                    className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12 focus-visible:ring-brand" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company" className="text-white/70 text-xs uppercase tracking-widest">Property / Venue <span className="normal-case tracking-normal text-white/30">(optional)</span></Label>
                <Input id="company" name="company" value={form.company} onChange={onChange} placeholder="e.g. Riverside Homestay" data-testid="contact-company"
                  className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12 focus-visible:ring-brand" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message" className="text-white/70 text-xs uppercase tracking-widest">Message</Label>
                <Textarea id="message" name="message" value={form.message} onChange={onChange} placeholder="How can we help?" rows={5} data-testid="contact-message"
                  className="bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-brand resize-none" />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-medium h-12 transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />}
                {loading ? "Sending..." : "Send message"}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutContact;
